import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import {
  geocodeBeach, fetchMarineForecast, fetchNOAATides,
  getCurrentHourIndex, calcSurfScore, metersToFeet,
  degreesToCompass, weatherCodeToDesc, weatherCodeToEmoji
} from '../../lib/weatherUtils'
import { Plus, X, RefreshCw, MapPin, Star, Search, Anchor } from 'lucide-react'

// ─── Add Beach Modal ──────────────────────────────────────────────────────────
function AddBeachModal({ schoolId, onClose, onAdded }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [noaaStation, setNoaaStation] = useState('')
  const [selected, setSelected] = useState(null)
  const [saving, setSaving] = useState(false)

  const search = async () => {
    if (!query.trim()) return
    setSearching(true)
    const res = await geocodeBeach(query)
    setResults(res)
    setSearching(false)
  }

  const handleSave = async () => {
    if (!selected) return
    setSaving(true)
    const { data } = await supabase.from('beach_locations').insert({
      school_id: schoolId,
      name: selected.name + (selected.admin1 ? `, ${selected.admin1}` : ''),
      latitude: selected.latitude,
      longitude: selected.longitude,
      noaa_station_id: noaaStation.trim() || null,
      is_primary: false,
    }).select().single()
    setSaving(false)
    onAdded(data)
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Add Beach Location</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 6 }}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Search for a beach or location</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-control" style={{ flex: 1 }} placeholder="e.g. North Shore Oahu, Bondi Beach..."
                value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search()} autoFocus />
              <button className="btn btn-primary" onClick={search} disabled={searching}>
                {searching ? '...' : <Search size={15} />}
              </button>
            </div>
          </div>

          {results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="form-label">Select location</label>
              {results.map((r, i) => (
                <button key={i} onClick={() => setSelected(r)} style={{
                  padding: '0.625rem 0.875rem', borderRadius: 8, border: '1.5px solid',
                  borderColor: selected?.name === r.name && selected?.latitude === r.latitude ? 'var(--teal)' : 'rgba(11,30,61,0.12)',
                  background: selected?.name === r.name && selected?.latitude === r.latitude ? 'rgba(14,170,164,0.06)' : 'white',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-dark)' }}>{r.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 2 }}>
                    {[r.admin1, r.admin2, r.country].filter(Boolean).join(', ')} · {r.latitude?.toFixed(4)}, {r.longitude?.toFixed(4)}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <Anchor size={11} style={{ display: 'inline', marginRight: 4 }} />
              NOAA Tide Station ID <span style={{ color: 'var(--text-light)', fontWeight: 400 }}>(optional — US beaches only)</span>
            </label>
            <input className="form-control" placeholder="e.g. 1612340 for Honolulu"
              value={noaaStation} onChange={e => setNoaaStation(e.target.value)} />
            <p style={{ fontSize: '0.72rem', color: 'var(--text-light)', marginTop: 4 }}>
              Find your station at <a href="https://tidesandcurrents.noaa.gov/stations.html" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>tidesandcurrents.noaa.gov</a>
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!selected || saving}>
            {saving ? 'Adding...' : 'Add Beach'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Surf Score Badge ─────────────────────────────────────────────────────────
function SurfScore({ score }) {
  const color = score >= 8 ? 'var(--teal)' : score >= 6 ? 'var(--gold)' : score >= 4 ? 'var(--ocean-bright)' : 'var(--coral)'
  const label = score >= 8 ? 'Excellent' : score >= 6 ? 'Good' : score >= 4 ? 'Fair' : 'Poor'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', border: `3px solid ${color}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: '0.5rem', color, fontWeight: 700, letterSpacing: '0.04em' }}>/10</div>
      </div>
      <div>
        <div style={{ fontSize: '0.875rem', fontWeight: 700, color }}>{label}</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-light)' }}>Surf score</div>
      </div>
    </div>
  )
}

// ─── Tide Chart ───────────────────────────────────────────────────────────────
function TideChart({ tides }) {
  if (!tides || tides.length === 0) return (
    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-light)', fontSize: '0.8rem' }}>
      No NOAA tide station linked. Add a station ID to see tides.
    </div>
  )

  const today = new Date().toDateString()
  const todayTides = tides.filter(t => new Date(t.t).toDateString() === today)
  const nextDays = {}
  tides.forEach(t => {
    const d = new Date(t.t).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    if (!nextDays[d]) nextDays[d] = []
    nextDays[d].push(t)
  })

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Today's Tides</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {todayTides.map((t, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 80, padding: '0.75rem', borderRadius: 10, textAlign: 'center',
              background: t.type === 'H' ? 'rgba(14,170,164,0.08)' : 'rgba(11,30,61,0.04)',
              border: `1px solid ${t.type === 'H' ? 'rgba(14,170,164,0.25)' : 'rgba(11,30,61,0.08)'}`,
            }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: t.type === 'H' ? 'var(--teal)' : 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                {t.type === 'H' ? '▲ High' : '▼ Low'}
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>
                {parseFloat(t.v).toFixed(1)}<span style={{ fontSize: '0.65rem', fontWeight: 400, color: 'var(--text-light)' }}>ft</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-mid)', marginTop: 4 }}>
                {new Date(t.t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>7-Day Tide Forecast</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {Object.entries(nextDays).slice(0, 7).map(([day, ts]) => (
            <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.4rem 0.5rem', borderRadius: 6, background: 'rgba(11,30,61,0.02)' }}>
              <div style={{ width: 90, fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-mid)' }}>{day}</div>
              <div style={{ flex: 1, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {ts.map((t, i) => (
                  <span key={i} style={{ fontSize: '0.72rem', color: t.type === 'H' ? 'var(--teal)' : 'var(--text-light)' }}>
                    {t.type === 'H' ? '▲' : '▼'} {parseFloat(t.v).toFixed(1)}ft {new Date(t.t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── 7-Day Forecast Strip ─────────────────────────────────────────────────────
function ForecastStrip({ marine, weather }) {
  if (!marine?.daily || !weather?.daily) return null
  const days = marine.daily.time || []

  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
      {days.map((day, i) => {
        const waveH = marine.daily.wave_height_max?.[i]
        const wavePer = marine.daily.wave_period_max?.[i]
        const wind = weather.daily.wind_speed_10m_max?.[i]
        const wxCode = Math.round(weather.daily.precipitation_probability_max?.[i] || 0) > 60 ? 61 : 0
        const score = calcSurfScore({ waveHeight: waveH, wavePeriod: wavePer, windSpeed: wind })
        const scoreColor = score >= 8 ? 'var(--teal)' : score >= 6 ? 'var(--gold)' : 'var(--coral)'
        const isToday = i === 0
        const label = isToday ? 'Today' : new Date(day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })
        const sunrise = weather.daily.sunrise?.[i]
        const sunset = weather.daily.sunset?.[i]

        return (
          <div key={day} style={{
            minWidth: 110, padding: '0.875rem 0.75rem', borderRadius: 12, textAlign: 'center',
            background: isToday ? 'rgba(14,170,164,0.08)' : 'white',
            border: `1px solid ${isToday ? 'rgba(14,170,164,0.3)' : 'rgba(11,30,61,0.07)'}`,
            flexShrink: 0
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: isToday ? 'var(--teal)' : 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{weatherCodeToEmoji(wxCode)}</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>
              {metersToFeet(waveH)}ft
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-light)', margin: '3px 0' }}>{wavePer?.toFixed(0) || '—'}s period</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-light)', marginBottom: 8 }}>💨 {wind?.toFixed(0) || '—'}mph</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: scoreColor }}>{score}/10</div>
            {sunrise && sunset && (
              <div style={{ fontSize: '0.6rem', color: 'var(--text-light)', marginTop: 6, lineHeight: 1.5 }}>
                🌅 {new Date(sunrise).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}<br />
                🌇 {new Date(sunset).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Current Conditions ───────────────────────────────────────────────────────
function CurrentConditions({ marine, weather }) {
  if (!marine?.hourly || !weather?.hourly) return null

  const hi = getCurrentHourIndex(marine.hourly.time || [])
  const whi = getCurrentHourIndex(weather.hourly.time || [])

  const waveH = marine.hourly.wave_height?.[hi]
  const wavePer = marine.hourly.wave_period?.[hi]
  const waveDir = marine.hourly.wave_direction?.[hi]
  const swellH = marine.hourly.swell_wave_height?.[hi]
  const swellPer = marine.hourly.swell_wave_period?.[hi]
  const swellDir = marine.hourly.swell_wave_direction?.[hi]
  const waterTemp = marine.hourly.sea_surface_temperature?.[hi]
  const wind = weather.hourly.wind_speed_10m?.[whi]
  const windDir = weather.hourly.wind_direction_10m?.[whi]
  const uv = weather.hourly.uv_index?.[whi]
  const wxCode = weather.hourly.weather_code?.[whi]

  const score = calcSurfScore({ waveHeight: waveH, wavePeriod: wavePer, windSpeed: wind })

  const stats = [
    { label: 'Wave Height', value: `${metersToFeet(waveH)}ft`, sub: degreesToCompass(waveDir) },
    { label: 'Wave Period', value: `${wavePer?.toFixed(0) || '—'}s`, sub: 'interval' },
    { label: 'Swell Height', value: `${metersToFeet(swellH)}ft`, sub: `${swellPer?.toFixed(0) || '—'}s ${degreesToCompass(swellDir)}` },
    { label: 'Wind', value: `${wind?.toFixed(0) || '—'}mph`, sub: degreesToCompass(windDir) },
    { label: 'Water Temp', value: waterTemp ? `${(waterTemp * 9/5 + 32).toFixed(0)}°F` : '—', sub: 'surface' },
    { label: 'UV Index', value: uv?.toFixed(0) || '—', sub: uv >= 8 ? '🔴 Very High' : uv >= 6 ? '🟠 High' : uv >= 3 ? '🟡 Moderate' : '🟢 Low' },
    { label: 'Conditions', value: weatherCodeToEmoji(wxCode), sub: weatherCodeToDesc(wxCode) },
  ]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <SurfScore score={score} />
        <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
          Updated {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: 'rgba(11,30,61,0.03)', borderRadius: 10, padding: '0.75rem', border: '1px solid rgba(11,30,61,0.06)' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-mid)', marginTop: 3 }}>{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Weather Page ────────────────────────────────────────────────────────
export default function WeatherPage({ isInstructor = false }) {
  const { user, profile } = useAuth()
  const [beaches, setBeaches] = useState([])
  const [selected, setSelected] = useState(null)
  const [weatherData, setWeatherData] = useState(null)
  const [tideData, setTideData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingBeaches, setLoadingBeaches] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [schoolId, setSchoolId] = useState(null)

  useEffect(() => {
    if (!user) return
    loadSchoolAndBeaches()
  }, [user])

  const loadSchoolAndBeaches = async () => {
    const { data: prof } = await supabase.from('profiles').select('school_id').eq('id', user.id).single()
    if (!prof?.school_id) { setLoadingBeaches(false); return }
    setSchoolId(prof.school_id)

    const { data } = await supabase.from('beach_locations').select('*').eq('school_id', prof.school_id).order('created_at')
    setBeaches(data || [])
    if (data?.length > 0) {
      const primary = data.find(b => b.is_primary) || data[0]
      setSelected(primary)
    }
    setLoadingBeaches(false)
  }

  const loadWeather = useCallback(async (beach) => {
    if (!beach) return
    setLoading(true)
    setWeatherData(null)
    setTideData(null)

    // Check cache (< 1 hour old)
    const { data: cache } = await supabase
      .from('weather_cache')
      .select('*')
      .eq('beach_id', beach.id)
      .order('fetched_at', { ascending: false })
      .limit(1)
      .single()

    const cacheAge = cache ? (Date.now() - new Date(cache.fetched_at).getTime()) / 60000 : 999

    if (cache && cacheAge < 60) {
      setWeatherData(cache.data)
    } else {
      const data = await fetchMarineForecast(beach.latitude, beach.longitude)
      setWeatherData(data)
      // Save to cache
      if (cache) {
        await supabase.from('weather_cache').update({ data, fetched_at: new Date().toISOString() }).eq('id', cache.id)
      } else {
        await supabase.from('weather_cache').insert({ beach_id: beach.id, data })
      }
    }

    // Fetch tides (always fresh)
    if (beach.noaa_station_id) {
      const tides = await fetchNOAATides(beach.noaa_station_id)
      setTideData(tides)
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    if (selected) loadWeather(selected)
  }, [selected, loadWeather])

  const handleSetPrimary = async (beach) => {
    await supabase.from('beach_locations').update({ is_primary: false }).eq('school_id', schoolId)
    await supabase.from('beach_locations').update({ is_primary: true }).eq('id', beach.id)
    setBeaches(b => b.map(x => ({ ...x, is_primary: x.id === beach.id })))
  }

  const handleDelete = async (beach) => {
    await supabase.from('beach_locations').delete().eq('id', beach.id)
    const remaining = beaches.filter(b => b.id !== beach.id)
    setBeaches(remaining)
    if (selected?.id === beach.id) setSelected(remaining[0] || null)
  }

  return (
    <div>
      {/* Beach selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
          {loadingBeaches ? (
            <div className="spinner" />
          ) : beaches.length === 0 ? (
            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
              {isInstructor ? 'No beaches added by your school yet.' : 'No beaches added yet. Add your first beach!'}
            </p>
          ) : beaches.map(b => (
            <button key={b.id} onClick={() => setSelected(b)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '0.4rem 0.875rem', borderRadius: 50, border: '1.5px solid',
              borderColor: selected?.id === b.id ? 'var(--teal)' : 'rgba(11,30,61,0.15)',
              background: selected?.id === b.id ? 'rgba(14,170,164,0.08)' : 'white',
              color: selected?.id === b.id ? 'var(--teal)' : 'var(--text-mid)',
              fontSize: '0.825rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
              fontFamily: 'DM Sans, sans-serif'
            }}>
              <MapPin size={11} /> {b.name}
              {b.is_primary && <Star size={10} fill="var(--gold)" color="var(--gold)" />}
            </button>
          ))}
        </div>
        {!isInstructor && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add Beach
          </button>
        )}
        {selected && (
          <button className="btn btn-ghost btn-sm" onClick={() => loadWeather(selected)} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
          </button>
        )}
      </div>

      {/* Beach actions (admin only) */}
      {selected && !isInstructor && (
        <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
          {!selected.is_primary && (
            <button className="btn btn-ghost btn-sm" onClick={() => handleSetPrimary(selected)}>
              <Star size={12} /> Set as Primary
            </button>
          )}
          <button className="btn btn-sm" onClick={() => handleDelete(selected)}
            style={{ background: 'rgba(232,97,74,0.08)', color: 'var(--coral)', border: '1px solid rgba(232,97,74,0.2)' }}>
            <X size={12} /> Remove Beach
          </button>
        </div>
      )}

      {/* Weather content */}
      {!selected ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light)' }}>
          <div style={{ fontSize: 48, marginBottom: '1rem' }}>🌊</div>
          <p>{isInstructor ? 'Your school hasn\'t added any beaches yet.' : 'Add a beach to see surf conditions.'}</p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p>Fetching surf conditions for {selected.name}...</p>
        </div>
      ) : weatherData ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MapPin size={16} color="var(--coral)" />
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 700 }}>{selected.name}</h2>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
              {selected.latitude.toFixed(4)}, {selected.longitude.toFixed(4)}
            </span>
          </div>

          {/* Current conditions */}
          <div className="card">
            <div className="card-header"><h3 style={{ fontWeight: 600 }}>Current Conditions</h3></div>
            <div className="card-body">
              <CurrentConditions marine={weatherData.marine} weather={weatherData.weather} />
            </div>
          </div>

          {/* 7-day forecast */}
          <div className="card">
            <div className="card-header"><h3 style={{ fontWeight: 600 }}>7-Day Surf Forecast</h3></div>
            <div className="card-body">
              <ForecastStrip marine={weatherData.marine} weather={weatherData.weather} />
            </div>
          </div>

          {/* Tides */}
          <div className="card">
            <div className="card-header">
              <h3 style={{ fontWeight: 600 }}>Tides</h3>
              {selected.noaa_station_id && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>NOAA Station {selected.noaa_station_id}</span>
              )}
            </div>
            <div className="card-body">
              <TideChart tides={tideData} />
            </div>
          </div>

          {/* Alerts */}
          {(() => {
            const hi = getCurrentHourIndex(weatherData.weather?.hourly?.time || [])
            const uv = weatherData.weather?.hourly?.uv_index?.[hi] || 0
            const whi = getCurrentHourIndex(weatherData.weather?.hourly?.time || [])
            const wind = weatherData.weather?.hourly?.wind_speed_10m?.[whi] || 0
            const wxCode = weatherData.weather?.hourly?.weather_code?.[whi] || 0
            const waveH = weatherData.marine?.hourly?.wave_height?.[hi] || 0
            const alerts = []
            if (uv >= 8) alerts.push({ icon: '☀️', msg: `UV Index ${uv.toFixed(0)} — Very High. Remind all students to apply SPF 50+.`, color: 'var(--coral)' })
            if (wind > 25) alerts.push({ icon: '💨', msg: `Strong winds at ${wind.toFixed(0)}mph. Consider rescheduling beginner lessons.`, color: 'var(--gold)' })
            if (wxCode >= 95) alerts.push({ icon: '⛈️', msg: 'Thunderstorm conditions. Cancel all lessons for safety.', color: 'var(--coral)' })
            if (waveH > 3) alerts.push({ icon: '🌊', msg: `Large ${metersToFeet(waveH)}ft surf. Not suitable for beginner lessons.`, color: 'var(--gold)' })
            if (alerts.length === 0) return null
            return (
              <div className="card">
                <div className="card-header"><h3 style={{ fontWeight: 600 }}>⚠️ Operational Alerts</h3></div>
                <div style={{ padding: '0.75rem 1.5rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {alerts.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '0.75rem 1rem', borderRadius: 8, background: `${a.color}10`, border: `1px solid ${a.color}30`, fontSize: '0.875rem', color: 'var(--text-mid)' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{a.icon}</span>
                      {a.msg}
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}
        </div>
      ) : null}

      {showAdd && schoolId && (
        <AddBeachModal
          schoolId={schoolId}
          onClose={() => setShowAdd(false)}
          onAdded={beach => { setBeaches(b => [...b, beach]); setSelected(beach) }}
        />
      )}
    </div>
  )
}
